import classNames from "classnames";
import { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import Dropdown from "src/app/components/dropdown/Dropdown";
import { FORM_TYPES, TARGET_GROUPS } from "src/app/modules/auth/constants";
import { INPUT_TYPE, ROUTES } from "src/config/constants";
import { ModalContext } from "src/contexts/ModalContext";
import { ApiResult } from "src/core/helpers/api/ApiResult";
import { AccountsService } from "src/core/services/auth";

import authCSS from "../Auth.module.css";

const RegisterForm = ({ changeForm = (formToDisplay = FORM_TYPES.LOGIN) => {}, onRegistration = (success: boolean) => {} }) => {
  const [currentState, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    targetGroup: 0,
    password: "",
    confirmPassword: "",
    terms: false,
    gdpr: false,
  });

  const [formErrors, setFormErrors] = useState<any>({});

  const modalData = useContext(ModalContext);

  const onInputChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    const inputType = event.target.type;
    const inputName = event.target.name;
    const inputValue = inputType === INPUT_TYPE.Checkbox ? event.target.checked : event.target.value;

    setFormData((currentState) => {
      return {
        ...currentState,
        [inputName]: inputValue,
      };
    });
  };

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();
    const returnUrl = `${window.location.origin}${ROUTES.verifyEmail}`;
    const errors = validate();
    setFormErrors(errors);

    if (errors && Object.keys(errors).length === 0 && Object.getPrototypeOf(errors) === Object.prototype) {
      AccountsService.register({
        firstName: currentState.firstName,
        lastName: currentState.lastName,
        email: currentState.email,
        phoneNumber: currentState.phoneNumber,
        targetGroup: TARGET_GROUPS.getText(currentState.targetGroup),
        password: currentState.password,
        confirmPassword: currentState.confirmPassword,
        acceptTerms: currentState.terms && currentState.gdpr,
        returnUrl: returnUrl,
      })
        .then((response) => {
          onRegistration(true);
          modalData?.triggerSuccess(response);
        })
        .catch((e: ApiResult) => {
          onRegistration(false);
          modalData?.triggerError(e);
        });
    }
  };

  const handleTargetGroupChange = (selectedTargetValue: number) => {
    setFormData((currentState) => {
      return {
        ...currentState,
        targetGroup: selectedTargetValue,
      };
    });
  };

  const validate = () => {
    const errors: any = {};
    const regexp =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

    if (!currentState.firstName) {
      errors.firstName = "* Potrebno je upisati ime";
    }
    if (!currentState.lastName) {
      errors.lastName = "* Potrebno je upisati prezime";
    }
    if (!currentState.phoneNumber) {
      errors.phoneNumber = "* Potrebno je upisati kontakt broj";
    }
    if (!currentState.email) {
      errors.email = "* Potrebno je upisati email";
    } else if (!regexp.test(currentState.email)) {
      errors.email = "* Provjerite ispravnost email adrese";
    }
    if (!currentState.password) {
      errors.password = "* Potrebno je upisati lozinku";
    } else if (currentState.password.length < 6) {
      errors.password = "* Lozinka mora imati više od 6 znakova";
    } else if (currentState.password.length > 64) {
      errors.password = "* Lozinka može imati najviše 64 znakova";
    }
    if (!currentState.confirmPassword) {
      errors.confirmPassword = "* Potrebno je potvrditi lozinku";
    } else if (currentState.confirmPassword !== currentState.password) {
      errors.confirmPassword = "* Lozinke se ne podudaraju";
    }
    if (currentState.gdpr === false) {
      errors.gdpr = "* Potrebno je označiti polje";
    }
    if (currentState.terms === false) {
      errors.terms = "* Potrebno je označiti polje";
    }
    if (currentState.targetGroup === 0) {
      errors.targetGroup = "* Potrebno je odabrati ciljnu skupinu";
    }
    return errors;
  };

  useEffect(() => {
    for (const property in formErrors) {
      if (formErrors[property] && formErrors[property].length > 0) {
        const newErrors = validate();
        setFormErrors((formErrors: any) => {
          return {
            ...formErrors,
            [property]: newErrors[property],
          };
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentState]);

  return (
    <div className="u-flex u-flex-column u-flex-items-center">
      <h3 className="u-t-primary-dark u-t-uppercase u-m0 u-pt10px u-pb40px">Registracija</h3>

      <form onSubmit={handleSubmit} className={classNames("u-col-xs-12", "u-col-sm-7", "u-col-md-9", "u-col-lg-10", authCSS.registerForm)}>
        <div className={classNames("u-flex u-flex-column", authCSS.regInputs)}>
          <div className="u-flex u-flex-row u-col-lg-12 u-mb30px">
            <div className="u-flex u-flex-column u-col-lg-6 u-pr5per">
              <label htmlFor="firstName" className="u-t-f14p u-ml10px">
                Ime
              </label>
              <input
                type="text"
                name="firstName"
                onChange={onInputChange}
                className={classNames("u-t-f15p", formErrors.firstName ? authCSS.errorInput : "")}
                value={currentState.firstName}
              ></input>
              <p className="u-m0 u-t-f12p u-t-error">{formErrors.firstName}</p>
            </div>
            <div className="u-flex u-flex-column u-col-lg-6">
              <label htmlFor="lastName" className="u-t-f14p u-ml10px">
                Prezime
              </label>
              <input
                type="text"
                name="lastName"
                onChange={onInputChange}
                className={classNames("u-t-f15p", formErrors.lastName ? authCSS.errorInput : "")}
                value={currentState.lastName}
              ></input>
              <p className="u-m0 u-t-f12p u-t-error">{formErrors.lastName}</p>
            </div>
          </div>
          <div className="u-flex u-flex-row u-col-lg-12 u-mb30px">
            <div className="u-flex u-flex-column u-col-lg-6 u-pr5per">
              <label htmlFor="dropdown" className="u-t-f14p u-ml10px">
                Ciljna skupina
              </label>
              <Dropdown
                items={TARGET_GROUPS.values}
                activeItemValue={currentState.targetGroup}
                defaultItem={null}
                placeholder={"odaberi"}
                removePlaceholder={true}
                handleChange={handleTargetGroupChange}
                customCss={classNames("u-t-f16p u-mb0", formErrors.targetGroup ? authCSS.errorSelect : "")}
              />
              <p className="u-m0 u-t-f12p u-t-error">{formErrors.targetGroup}</p>
            </div>

            <div className="u-flex u-flex-column u-col-lg-6">
              <label htmlFor="phoneNumber" className="u-t-f14p u-ml10px">
                Kontakt broj
              </label>
              <input
                type="text"
                name="phoneNumber"
                onChange={onInputChange}
                className={classNames("u-t-f15p", formErrors.phoneNumber ? authCSS.errorInput : "")}
                value={currentState.phoneNumber}
              ></input>
              <p className="u-m0 u-t-f12p u-t-error">{formErrors.phoneNumber}</p>
            </div>
          </div>
          <div className="u-flex u-flex-row u-col-lg-12 u-mb30px">
            <div className="u-flex u-flex-column u-col-lg-6 u-pr5per">
              <label htmlFor="email" className="u-t-f14p u-ml10px">
                Email
              </label>
              <input
                type="text"
                name="email"
                onChange={onInputChange}
                className={classNames("u-t-f15p", formErrors.email ? authCSS.errorInput : "")}
                value={currentState.email}
              ></input>
              <p className="u-m0 u-t-f12p u-t-error">{formErrors.email}</p>
            </div>
            <div className="u-flex u-flex-column u-col-lg-6">
              <label htmlFor="password" className="u-t-f14p u-ml10px">
                Lozinka
              </label>
              <input
                type="password"
                name="password"
                onChange={onInputChange}
                className={classNames("u-t-f15p", formErrors.password ? authCSS.errorInput : "")}
                value={currentState.password}
              ></input>
              <p className="u-m0 u-t-f12p u-t-error">{formErrors.password}</p>
            </div>
          </div>
          <div className="u-flex u-flex-row u-col-lg-12 u-mb30px">
            <div className="u-flex u-flex-column u-col-lg-6 u-pr5per">
              <label htmlFor="confirmPassword" className="u-t-f14p u-ml10px">
                Ponovite lozinku
              </label>
              <input
                type="password"
                name="confirmPassword"
                onChange={onInputChange}
                className={classNames("u-t-f15p", formErrors.confirmPassword ? authCSS.errorInput : "")}
                value={currentState.confirmPassword}
              ></input>
              <p className="u-m0 u-t-f12p u-t-error">{formErrors.confirmPassword}</p>
            </div>
            <div className="u-flex u-flex-column u-col-lg-6 u-flex-content-center">
              <div className="u-mt15px u-mb10px">
                <input
                  type="checkbox"
                  name="gdpr"
                  onChange={onInputChange}
                  id="privacy"
                  className={classNames("u-mr5per", formErrors.gdpr ? authCSS.errorInput : "")}
                />

                <span>
                  Dajem
                  <button className={classNames("u-t-underline", "u-cursor-pointer", "u-t-primary")}>
                    <Link to={ROUTES.privacyPolicy} target={"_blank"} className="u-t-nodecoration u-t-primary u-t-w400 u-t-lh15 u-cursor-pointer">
                      privolu za obradu osobnih podataka
                    </Link>
                  </button>
                </span>
                <p className="u-m0 u-t-f12p u-t-error">{formErrors.gdpr}</p>
              </div>

              <div>
                <input
                  type="checkbox"
                  name="terms"
                  onChange={onInputChange}
                  id="conditions"
                  className={classNames("u-mr5per", formErrors.terms ? authCSS.errorInput : "")}
                />
                <span>
                  Prihvaćam
                  <button className={classNames("u-t-underline", "u-cursor-pointer", "u-t-primary")}>
                    <Link to={ROUTES.termsAndCond} target={"_blank"} className="u-t-nodecoration u-t-primary u-t-w400 u-t-lh15 u-cursor-pointer">
                      uvjete i pravila korištenja
                    </Link>
                  </button>
                </span>
                <p className="u-m0 u-t-f12p u-t-error">{formErrors.terms}</p>
              </div>
            </div>
          </div>
        </div>

        <div className={classNames("u-flex", "u-flex-content-center", "u-mb30px", "u-col-lg-3", authCSS.buttonWrap)}>
          <button
            type="submit"
            className={classNames(
              "u-bg-primary",
              "u-t-white",
              "u-cursor-pointer",
              "u-t-uppercase",
              "u-col-xs-12",
              "u-font-family-inh",
              "u-t-f16p",
              "u-t-lh24",
              authCSS.button
            )}
          >
            Registrirajte se
          </button>
        </div>
      </form>

      <hr className="u-col-xs-12 u-col-sm-7 u-col-md-9 u-col-lg-10 u-bt0 u-br0 ubl0 u-line-base" />

      <div className="u-t-f14p u-mb10px">
        <div>
          Imate račun?
          <button className={classNames("u-font-family-inh", "u-t-underline", "u-cursor-pointer", "u-t-primary")} onClick={() => changeForm(FORM_TYPES.LOGIN)}>
            Prijavite se
          </button>
        </div>
      </div>

      <div className="u-t-halign-c u-t-f14p u-mb30px">
        <button
          className={classNames("u-font-family-inh", "u-t-underline", "u-cursor-pointer", "u-t-primary")}
          onClick={() => changeForm(FORM_TYPES.FORGOTTEN_PASSWORD)}
        >
          Zaboravljena lozinka?
        </button>
      </div>
    </div>
  );
};

export default RegisterForm;
